import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import {
  type AtticErrorFragment,
  type AtticLocationInput,
  useAtticLocationCreateMutation,
  useAtticLocationDetailsQuery,
  useAtticLocationUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import AtticLocationDetailsPage, {
  type AtticLocationFormData,
} from "../components/AtticLocationDetailsPage/AtticLocationDetailsPage";
import { locationMessages } from "../messages";
import { atticLocationUrl } from "../urls";

/**
 * What the form sends. On create, every field; on edit, the same, since the
 * API leaves a field it is not given alone and the form always holds them all.
 */
export const formToLocationInput = (data: AtticLocationFormData): AtticLocationInput => ({
  code: data.code.trim().toUpperCase(),
  name: data.name.trim(),
  kind: data.kind,
  parent: data.parent.trim().toUpperCase() || null,
  isActive: data.isActive,
  notes: data.notes,
});

/** `code` null is the create page; otherwise the location with that code. */
export const AtticLocationDetailsView = ({ code }: { code: string | null }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [errors, setErrors] = useState<AtticErrorFragment[]>([]);
  const [saveState, setSaveState] = useState<ConfirmButtonTransitionState>("default");
  const { data, loading } = useAtticLocationDetailsQuery({
    displayLoader: true,
    variables: { code: code ?? "" },
    skip: code === null,
  });
  const location = data?.atticLocation ?? null;

  const finish = (refused: AtticErrorFragment[], saved: boolean) => {
    setErrors(refused);
    setSaveState(saved ? "success" : "error");
  };
  const [createLocation] = useAtticLocationCreateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticLocationCreate;
      const refused = payload?.errors ?? [];
      const created = payload?.location;

      finish(refused, !refused.length && !!created);

      if (!refused.length && created) {
        notify({ status: "success", text: intl.formatMessage(locationMessages.created) });
        navigate(atticLocationUrl(created.code), { replace: true });
      }
    },
    onError: () => setSaveState("error"),
  });
  const [updateLocation] = useAtticLocationUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticLocationUpdate;
      const refused = payload?.errors ?? [];
      const saved = payload?.location;

      finish(refused, !refused.length && !!saved);

      if (!refused.length && saved) {
        notify({ status: "success", text: intl.formatMessage(locationMessages.saved) });

        // A new code is a new URL.
        if (saved.code !== code) {
          navigate(atticLocationUrl(saved.code), { replace: true });
        }
      }
    },
    onError: () => setSaveState("error"),
  });

  if (code !== null && loading && !data) {
    return (
      <Box padding={6}>
        <Skeleton />
      </Box>
    );
  }

  if (code !== null && !location) {
    return (
      <Box padding={6}>
        <Text size={4} data-test-id="attic-location-missing">
          {intl.formatMessage(locationMessages.notFound)}
        </Text>
      </Box>
    );
  }

  return (
    <AtticLocationDetailsPage
      location={location}
      errors={errors}
      disabled={saveState === "loading"}
      saveButtonBarState={saveState}
      onSubmit={form => {
        setErrors([]);
        setSaveState("loading");

        const input = formToLocationInput(form);

        if (location) {
          updateLocation({ variables: { id: location.id, input } });
        } else {
          createLocation({ variables: { input } });
        }
      }}
    />
  );
};

export default AtticLocationDetailsView;
